import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject, Observable, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Storage } from './storage.service';

export interface ModuleChangeEvent {
  type: string;
  module: string;
  action: string;
  data: any;
  timestamp: number;
}

export interface RolePermissionUpdateEvent {
  type: string;
  roleId: number;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class RealtimeService implements OnDestroy {
  private eventSource: EventSource | null = null;
  private reconnectTimer: any = null;
  private reconnectDelay = 2000;
  private maxReconnectDelay = 30000;
  private isConnecting = false;
  private moduleBroadcastChannel: BroadcastChannel | null = null;

  private rolePermissionsUpdatedSubject = new Subject<RolePermissionUpdateEvent>();
  public rolePermissionsUpdated$: Observable<RolePermissionUpdateEvent> = this.rolePermissionsUpdatedSubject.asObservable();

  private moduleDataChangedSubject = new Subject<ModuleChangeEvent>();
  public moduleDataChanged$: Observable<ModuleChangeEvent> = this.moduleDataChangedSubject.asObservable();

  constructor(
    private storage: Storage,
    private ngZone: NgZone
  ) {
    this.initCrossTabSync();
  }

  private initCrossTabSync(): void {
    if (typeof window === 'undefined') return;

    // 1. Cross-tab BroadcastChannel for module events
    if ('BroadcastChannel' in window) {
      try {
        this.moduleBroadcastChannel = new BroadcastChannel('fixura_module_sync');
        this.moduleBroadcastChannel.onmessage = (msgEvent) => {
          if (msgEvent.data && msgEvent.data.type === 'MODULE_CHANGED') {
            this.ngZone.run(() => {
              this.moduleDataChangedSubject.next(msgEvent.data);
            });
          }
        };
      } catch (e) {
        console.warn('[Realtime] BroadcastChannel for module sync not supported', e);
      }
    }

    // 2. Storage event listener fallback (across browser windows/tabs)
    window.addEventListener('storage', (event) => {
      if (event.key === 'fixura_module_event' && event.newValue) {
        try {
          const payload: ModuleChangeEvent = JSON.parse(event.newValue);
          if (payload && payload.type === 'MODULE_CHANGED') {
            this.ngZone.run(() => {
              this.moduleDataChangedSubject.next(payload);
            });
          }
        } catch (e) {}
      }
    });
  }

  /**
   * Broadcasts a local module change across tabs immediately
   */
  public notifyModuleChange(module: string, action: string, data: any = {}): void {
    const event: ModuleChangeEvent = {
      type: 'MODULE_CHANGED',
      module: module.toUpperCase().trim(),
      action: action.toUpperCase().trim(),
      data,
      timestamp: Date.now()
    };
    this.moduleDataChangedSubject.next(event);
    if (this.moduleBroadcastChannel) {
      try {
        this.moduleBroadcastChannel.postMessage(event);
      } catch (e) {}
    }
    try {
      localStorage.setItem('fixura_module_event', JSON.stringify(event));
      setTimeout(() => {
        try { localStorage.removeItem('fixura_module_event'); } catch (e) {}
      }, 500);
    } catch (e) {}
  }

  /**
   * Subscribes to module changes. Accepts single or multiple module names.
   * Matches case-insensitively. Wildcards '*' or 'ALL' match any module.
   */
  public onModuleChange(
    modules: string | string[],
    callback: (event: ModuleChangeEvent) => void
  ): Subscription {
    const targets = (Array.isArray(modules) ? modules : [modules]).map(m => m.toUpperCase().trim());
    return this.moduleDataChanged$.subscribe((event) => {
      const currentMod = (event.module || '').toUpperCase().trim();
      const match = targets.includes('*') || targets.includes('ALL') || targets.includes(currentMod);
      if (match) {
        callback(event);
      }
    });
  }

  public connect(): void {
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') return;
    if (this.eventSource && this.eventSource.readyState === EventSource.OPEN) return;
    if (this.isConnecting) return;

    const token = this.storage.getToken();
    if (!token) return;

    // Check if access token is expired
    if (this.storage.isTokenExpired(token)) {
      if (!this.storage.isLoggedIn()) {
        this.disconnect();
        return;
      }
      // Stale access token; wait for refresh before connecting
      this.scheduleReconnect();
      return;
    }

    this.isConnecting = true;
    this.cleanup();

    const streamUrl = `${environment.apiUrl}/realtime/stream?token=${encodeURIComponent(token)}`;

    this.ngZone.runOutsideAngular(() => {
      try {
        this.eventSource = new EventSource(streamUrl);

        this.eventSource.onopen = () => {
          this.isConnecting = false;
          this.reconnectDelay = 2000;
          console.debug('[Realtime] SSE Connection established');
        };

        this.eventSource.addEventListener('ROLE_PERMISSIONS_UPDATED', (event: MessageEvent) => {
          try {
            const data: RolePermissionUpdateEvent = JSON.parse(event.data);
            this.ngZone.run(() => {
              this.rolePermissionsUpdatedSubject.next(data);
            });
          } catch (e) {
            console.warn('[Realtime] Failed to parse ROLE_PERMISSIONS_UPDATED payload', e);
          }
        });

        this.eventSource.addEventListener('MODULE_CHANGED', (event: MessageEvent) => {
          try {
            const data: ModuleChangeEvent = JSON.parse(event.data);
            this.ngZone.run(() => {
              this.moduleDataChangedSubject.next(data);
            });
          } catch (e) {
            console.warn('[Realtime] Failed to parse MODULE_CHANGED payload', e);
          }
        });

        this.eventSource.addEventListener('HEARTBEAT', () => {
          // Keep-alive heartbeat acknowledgment
        });

        this.eventSource.onerror = (error) => {
          this.isConnecting = false;
          console.debug('[Realtime] SSE connection error, attempting reconnection in ' + this.reconnectDelay + 'ms', error);
          this.cleanup();
          if (this.storage.isLoggedIn()) {
            this.scheduleReconnect();
          } else {
            this.disconnect();
          }
        };

      } catch (err) {
        this.isConnecting = false;
        if (this.storage.isLoggedIn()) {
          this.scheduleReconnect();
        } else {
          this.disconnect();
        }
      }
    });
  }

  public disconnect(): void {
    this.cleanup();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.moduleBroadcastChannel) {
      this.moduleBroadcastChannel.close();
      this.moduleBroadcastChannel = null;
    }
  }

  private cleanup(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    if (!this.storage.isLoggedIn()) return;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.storage.isLoggedIn()) return;
      this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, this.maxReconnectDelay);
      this.connect();
    }, this.reconnectDelay);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
